import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';

const SystemStatusIndicator = ({ isExpanded = false, onToggle }) => {
  const [systemStatus, setSystemStatus] = useState({
    overall: 'operational',
    services: [
      {
        id: 'telephony',
        name: 'Telephony System',
        status: 'operational',
        uptime: 99.9,
        lastCheck: new Date(),
        description: 'Call routing and recording services'
      },
      {
        id: 'payment',
        name: 'Payment Processor',
        status: 'operational',
        uptime: 99.8,
        lastCheck: new Date(),
        description: 'Billing and transaction processing'
      },
      {
        id: 'crm',
        name: 'CRM Integration',
        status: 'degraded',
        uptime: 97.5,
        lastCheck: new Date(),
        description: 'Customer relationship management sync'
      },
      {
        id: 'analytics',
        name: 'Analytics Engine',
        status: 'operational',
        uptime: 99.7,
        lastCheck: new Date(),
        description: 'Real-time reporting and insights'
      },
      {
        id: 'auction',
        name: 'Auction Engine',
        status: 'operational',
        uptime: 99.9,
        lastCheck: new Date(),
        description: 'Real-time bidding system'
      }
    ]
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setSystemStatus(prev => ({
        ...prev,
        services: prev?.services?.map(service => ({
          ...service,
          lastCheck: new Date(),
          uptime: service?.status === 'operational' 
            ? Math.min(99.9, service?.uptime + Math.random() * 0.1)
            : Math.max(95.0, service?.uptime + Math.random() * 0.5)
        }))
      }));
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'operational':
        return 'text-success';
      case 'degraded':
        return 'text-warning';
      case 'outage':
        return 'text-error';
      default:
        return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'operational':
        return 'CheckCircle';
      case 'degraded':
        return 'AlertTriangle';
      case 'outage':
        return 'XCircle';
      default:
        return 'HelpCircle';
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'operational':
        return 'bg-success/10 text-success';
      case 'degraded':
        return 'bg-warning/10 text-warning';
      case 'outage':
        return 'bg-error/10 text-error';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const formatUptime = (uptime) => {
    return `${uptime?.toFixed(1)}%`;
  };

  const formatLastCheck = (date) => {
    const now = new Date();
    const diff = now - date;
    const seconds = Math.floor(diff / 1000);
    
    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  };

  const overallStatus = systemStatus?.services?.some(s => s?.status === 'outage') 
    ? 'outage' 
    : systemStatus?.services?.some(s => s?.status === 'degraded') 
    ? 'degraded' :'operational';

  return (
    <div className="fixed bottom-4 right-4 z-1000">
      {/* Compact Status Indicator */}
      <button
        onClick={onToggle}
        className="flex items-center space-x-2 px-3 py-2 bg-card border border-border rounded-lg shadow-card hover:shadow-modal transition-smooth"
        title="System Status"
      >
        <div className="flex items-center space-x-2">
          <Icon 
            name={getStatusIcon(overallStatus)} 
            size={16} 
            className={getStatusColor(overallStatus)} 
          />
          <span className="text-sm font-medium text-foreground">System Status</span>
        </div>
        <Icon 
          name={isExpanded ? "ChevronDown" : "ChevronUp"} 
          size={16} 
          className="text-muted-foreground" 
        />
      </button>
      {/* Expanded Status Panel */}
      {isExpanded && (
        <>
          <div 
            className="fixed inset-0 z-1000"
            onClick={onToggle}
          />
          <div className="absolute bottom-full right-0 mb-2 w-80 bg-card border border-border rounded-lg shadow-modal z-1100">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="flex items-center space-x-2">
                <Icon name="Activity" size={20} className="text-foreground" />
                <h3 className="font-semibold text-foreground">System Health</h3>
              </div>
              <div className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(overallStatus)}`}>
                {overallStatus?.charAt(0)?.toUpperCase() + overallStatus?.slice(1)}
              </div>
            </div>

            {/* Services List */}
            <div className="max-h-64 overflow-y-auto">
              <div className="divide-y divide-border">
                {systemStatus?.services?.map((service) => (
                  <div key={service?.id} className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <Icon 
                          name={getStatusIcon(service?.status)} 
                          size={16} 
                          className={getStatusColor(service?.status)} 
                        />
                        <span className="text-sm font-medium text-foreground">
                          {service?.name}
                        </span>
                      </div>
                      <div className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusBadgeColor(service?.status)}`}>
                        {formatUptime(service?.uptime)}
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mb-1">
                      {service?.description}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Last checked: {formatLastCheck(service?.lastCheck)}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="p-3 border-t border-border bg-muted/30">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Auto-refresh: 30s</span>
                <button className="text-accent hover:text-accent/80 transition-smooth">
                  View Status Page
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SystemStatusIndicator;